"use client";
import React, { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

interface Property {
  id: number;
  title: string;
  location: string;
  price: string;
  highlight: string;
  tag: "Premium" | "Affordable" | "High Growth";
  image: string;
  alt: string;
}

const PROPERTIES: Property[] = [
  {
    id: 1,
    title: "Umerabad Residential Plot",
    location: "Umerabad, Kashmir",
    price: "₹18L – ₹35L",
    highlight: "Peaceful locality. Fast-developing zone. Strong future value.",
    tag: "Affordable",
    image: "https://images.unsplash.com/photo-1500534314209-a25ddb2bd429",
    alt: "Open land plot in Umerabad Kashmir with green surroundings and clear blue sky",
  },
  {
    id: 2,
    title: "Mustafabad Land Parcel",
    location: "Mustafabad, Kashmir",
    price: "₹20L – ₹40L",
    highlight: "Residential growth corridor. Legal clear title. Ready to register.",
    tag: "High Growth",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4",
    alt: "Scenic land parcel in Mustafabad Kashmir with mountain backdrop and open fields",
  },
  {
    id: 3,
    title: "Bemina Urban Plot",
    location: "Bemina, Srinagar",
    price: "₹35L – ₹65L",
    highlight: "Prime Srinagar suburb. High demand. Excellent connectivity.",
    tag: "Premium",
    image: "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23",
    alt: "Urban plot in Bemina Srinagar with city infrastructure and green landscape visible",
  },
  {
    id: 4,
    title: "Shalteng Riverside Plot",
    location: "Shalteng, Srinagar",
    price: "₹30L – ₹55L",
    highlight: "Near Jhelum river. Scenic views. Rapidly appreciating area.",
    tag: "High Growth",
    image: "https://images.unsplash.com/photo-1548013146-72479768bada",
    alt: "Riverside land in Shalteng Srinagar with lush greenery and Jhelum river in background",
  },
  {
    id: 5,
    title: "Lawaypora Investment Plot",
    location: "Lawaypora, Srinagar",
    price: "₹28L – ₹50L",
    highlight: "Near airport road. Strategic location. High rental potential.",
    tag: "High Growth",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4",
    alt: "Investment plot in Lawaypora Srinagar near airport road with open land and clear skies",
  },
  {
    id: 6,
    title: "Wanabal Residential Land",
    location: "Wanabal, Kashmir",
    price: "₹15L – ₹28L",
    highlight: "Affordable entry point. Verified title. Ideal first investment.",
    tag: "Affordable",
    image: "https://images.unsplash.com/photo-1500534314209-a25ddb2bd429",
    alt: "Residential land in Wanabal Kashmir with natural greenery and mountain views",
  },
  {
    id: 7,
    title: "Maloora Premium Plot",
    location: "Maloora, Srinagar",
    price: "₹40L – ₹75L",
    highlight: "Established neighbourhood. Close to city amenities. Strong demand.",
    tag: "Premium",
    image: "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23",
    alt: "Premium plot in Maloora Srinagar with well-developed surroundings and clear title",
  },
  {
    id: 8,
    title: "Mujgund Growth Zone Plot",
    location: "Mujgund, Srinagar",
    price: "₹22L – ₹42L",
    highlight: "Emerging residential zone. Infrastructure expansion underway.",
    tag: "High Growth",
    image: "https://images.unsplash.com/photo-1548013146-72479768bada",
    alt: "Growth zone land in Mujgund Srinagar with new infrastructure development in background",
  },
  {
    id: 9,
    title: "Sumbal Valley Plot",
    location: "Sumbal, Bandipora",
    price: "₹12L – ₹25L",
    highlight: "Scenic valley location. Low entry cost. High long-term upside.",
    tag: "Affordable",
    image: "https://images.unsplash.com/photo-1500534314209-a25ddb2bd429",
    alt: "Valley plot in Sumbal Bandipora Kashmir with lush green fields and mountain scenery",
  },
  {
    id: 10,
    title: "Safapora Countryside Land",
    location: "Safapora, Ganderbal",
    price: "₹14L – ₹30L",
    highlight: "Tranquil countryside. Growing tourism belt. Appreciating fast.",
    tag: "Affordable",
    image: "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23",
    alt: "Countryside land in Safapora Ganderbal Kashmir with open fields and scenic mountain views",
  },
];

const tagColors: Record<string, string> = {
  "Premium": "bg-amber/10 text-amber",
  "Affordable": "bg-pine/10 text-pine",
  "High Growth": "bg-fog/60 text-pine",
};

export default function GalleryGrid() {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );
    cardRefs.current.forEach((card) => { if (card) observer.observe(card); });
    return () => observer.disconnect();
  }, []);

  return (
    <section id="properties" className="bg-fog/30 py-24 px-6 relative">
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-pine-light mb-4 block">
              Featured Properties
            </span>
            <h2 className="font-serif text-display-sm text-pine font-light leading-tight">
              Handpicked plots.<br />
              <em className="italic">Verified and ready.</em>
            </h2>
          </div>
          <div className="max-w-xs">
            <p className="text-sm text-pine-light font-light leading-relaxed">
              Every listing is legally verified. We only show what we&apos;d invest in ourselves.
            </p>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PROPERTIES.map((property, index) => (
            <div
              key={property.id}
              ref={(el) => { cardRefs.current[index] = el; }}
              className="reveal bg-linen rounded-3xl overflow-hidden shadow-card"
              style={{ transitionDelay: `${index * 0.1}s` }}>

              {/* Image */}
              <div className="relative h-52 overflow-hidden">
                <AppImage
                  src={property.image}
                  alt={property.alt}
                  fill
                  className="object-cover w-full h-full hover:scale-105 transition-transform duration-700" />
                <div className="absolute top-3 left-3">
                  <span className={`text-[10px] font-medium tracking-wide px-2.5 py-1 rounded-full ${tagColors[property.tag]}`}>
                    {property.tag}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="font-serif text-lg font-light text-pine mb-1">{property.title}</h3>
                <div className="flex items-center gap-1.5 mb-3">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#6B7563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span className="text-xs text-pine-light">{property.location}</span>
                </div>
                <p className="text-xs text-pine-light font-light leading-relaxed mb-4">{property.highlight}</p>
                <div className="flex items-center justify-between pt-3 border-t border-fog/60">
                  <span className="font-serif text-base font-medium text-pine">{property.price}</span>
                  <a href="#contact" className="text-xs font-medium text-amber hover:underline">Enquire →</a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Why Kashmir section */}
        <div className="mt-24">
          <div className="text-center mb-12">
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-pine-light mb-4 block">
              Why Kashmir
            </span>
            <h2 className="font-serif text-display-sm text-pine font-light">
              Five reasons to invest <em className="italic">now.</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { number: "01", title: "Tourism Boom", detail: "Kashmir welcomed 2.1 crore tourists in 2023. Demand for land near tourist zones is rising fast." },
              { number: "02", title: "Land Scarcity", detail: "Geographical limits mean supply is fixed. As demand grows, prices only move one way." },
              { number: "03", title: "Infrastructure Growth", detail: "New highways, rail links, and the Srinagar airport expansion are unlocking previously inaccessible areas." },
              { number: "04", title: "Strong Appreciation", detail: "Prime Kashmir land has appreciated 18–25% annually over the last 5 years in key corridors." },
              { number: "05", title: "NRI Eligibility", detail: "NRIs can now legally purchase land in J&K. A historic window that is still early." },
            ].map((reason) => (
              <div key={reason.number} className="bg-linen rounded-2xl p-6 shadow-card">
                <div className="font-serif text-3xl font-light text-amber mb-3">{reason.number}</div>
                <h4 className="font-medium text-pine text-sm mb-2">{reason.title}</h4>
                <p className="text-xs text-pine-light font-light leading-relaxed">{reason.detail}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Investment Angle */}
        <div className="mt-24 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-pine rounded-3xl p-10 text-linen">
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-linen/50 mb-4 block">Luxury Investments</span>
            <h3 className="font-serif text-2xl font-light mb-4 leading-tight">
              Premium plots for<br />serious investors.
            </h3>
            <p className="text-sm text-linen/70 font-light leading-relaxed mb-6">
              River-facing land in Pahalgam. Hillside plots in Gulmarg. These are not just properties — they are legacy assets. Limited availability. High appreciation. Generational value.
            </p>
            <div className="text-sm font-medium text-amber">Starting from ₹45 Lakhs →</div>
          </div>
          <div className="bg-amber/10 border border-amber/20 rounded-3xl p-10">
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-pine-light mb-4 block">Affordable Entry</span>
            <h3 className="font-serif text-2xl font-light text-pine mb-4 leading-tight">
              Start small.<br />Think long-term.
            </h3>
            <p className="text-sm text-pine-light font-light leading-relaxed mb-6">
              You don&apos;t need crores to invest in Kashmir. Plots starting at ₹15 Lakhs in high-growth zones. Verified. Legal. Ready to register. Your first step into real estate doesn&apos;t have to be expensive.
            </p>
            <div className="text-sm font-medium text-pine">Starting from ₹15 Lakhs →</div>
          </div>
        </div>
      </div>
    </section>
  );
}